/**
 * Catálogos configuráveis — `03-endpoints-draft.md §4`: **cinco recursos, um
 * contrato**.
 *
 * `/pontos-conversao` · `/produtos-associados` · `/motivos-agendamento` ·
 * `/motivos-venda-nao-realizada` · `/tags`
 *
 * Os cinco compartilham a forma base (`id · titulo · ordem · ativo · auditoria`,
 * `02-domains.md §7`) e divergem em dois ou três campos. Modelar como cinco
 * contratos independentes produziria cinco variações da mesma correção — e são
 * exatamente as telas que a operação usa para consertar dado errado.
 *
 * **A tela nunca hardcoda estes valores.** O objetivo do módulo é que "Site",
 * "Contato Inválido" e "Apartamento" sejam dado, não código: quem opera cadastra
 * sem release.
 */

/** Os cinco recursos, na forma em que aparecem na URL. */
export const CATALOGOS = [
  'pontos-conversao',
  'produtos-associados',
  'motivos-agendamento',
  'motivos-venda-nao-realizada',
  'tags',
] as const

export type NomeCatalogo = (typeof CATALOGOS)[number]

const CONJUNTO_CATALOGOS: ReadonlySet<string> = new Set(CATALOGOS)

export function ehNomeCatalogo(valor: string): valor is NomeCatalogo {
  return CONJUNTO_CATALOGOS.has(valor)
}

export const ROTULO_CATALOGO: Readonly<Record<NomeCatalogo, string>> = {
  'pontos-conversao': 'Pontos de Conversão',
  'produtos-associados': 'Produtos Associados',
  'motivos-agendamento': 'Motivos de Agendamento',
  'motivos-venda-nao-realizada': 'Motivos de Venda Não Realizada',
  tags: 'Tags',
}

/**
 * Quais catálogos são **estruturais** — dimensão de relatório, e por isso
 * exclusivos do Administrador (`04-roles §3.6`, permissão
 * `CATALOGO_GERENCIAR_ESTRUTURAL`).
 *
 * Ponto de conversão é a dimensão-chave dos relatórios e produto associado é o que
 * amarra lead a empreendimento: renomear ou desativar um deles reescreve a leitura
 * de todo histórico. Os outros três são vocabulário operacional do dia a dia, e o
 * gestor precisa poder acrescentar um motivo sem abrir chamado
 * (`CATALOGO_GERENCIAR_OPERACIONAL`).
 */
export const CATALOGOS_ESTRUTURAIS: ReadonlyArray<NomeCatalogo> = [
  'pontos-conversao',
  'produtos-associados',
]

export function catalogoEhEstrutural(catalogo: NomeCatalogo): boolean {
  return CATALOGOS_ESTRUTURAIS.includes(catalogo)
}

/**
 * Registro de catálogo. Os campos opcionais existem só em alguns:
 *
 * | Campo | Onde existe | Para quê |
 * |---|---|---|
 * | `cor` | `motivos-agendamento` | cor do compromisso no calendário (TELA-08/12) |
 * | `contabilizarRelatorio` | `motivos-agendamento` | motivo que não entra na contagem de agendamentos (TELA-12) |
 * | `codigoReferencia` | `produtos-associados` | código do empreendimento (TELA-14) |
 *
 * Ausentes, e não `null`, nos catálogos onde não se aplicam: `null` afirmaria que o
 * campo existe e está vazio, e a tela genérica renderizaria um campo de cor para
 * uma tag.
 */
export interface ItemCatalogo {
  readonly id: number
  readonly titulo: string
  readonly ordem: number
  readonly ativo: boolean
  readonly cor?: string
  readonly contabilizarRelatorio?: boolean
  readonly codigoReferencia?: string | null
  /**
   * RN-41 — o registro já foi usado por algum lead, interação ou agendamento.
   *
   * A tela usa para **não oferecer exclusão** (que não existe) e para avisar que
   * desativar não apaga o histórico. Calculado no servidor: a tela não tem como
   * saber, e cada uma inventaria uma resposta.
   */
  readonly emUso: boolean
}
